import { action, extendObservable, runInAction } from 'mobx';
import FetchService from '../../../core/services/FetchService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import NotificationService from '../../../core/services/NotificationService';
import FormService from '../../../core/services/FormService';

class LocalizationListService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      items: [],
      pagination: {
        current: 1,
        pageSize: 50,
        total: 0,
      },
      firstLocaleSelect: 'pt',
      secondLocaleSelect: 'es',
      sort: {
        field: 'key',
        order: 'asc',
      },
    });
    this.form.setInitialValues({
      type: 'NEW_STUDENT_WEB',
      onlyEmpty: false,
      firstLocaleSelect: 'pt',
      secondLocaleSelect: 'es',
    });
  }

  init = () => {
    runInAction(() => {
      this.form.setInitialValues({
        type: 'NEW_STUDENT_WEB',
        onlyEmpty: false,
        firstLocaleSelect: 'pt',
        secondLocaleSelect: 'es',
      });
    });
    this.firstLocaleSelect = 'pt';
    this.secondLocaleSelect = 'es';
    this.pagination.current = 1;
  };

  load = action(() => {
    this.fetch
      .fetch({
        url: '/localization-items',
        query: {
          onlyEmpty: this.form.getValue('onlyEmpty'),
          locales: ['en', this.firstLocaleSelect, this.secondLocaleSelect],
          query: {
            type: this.form.getValue('type'),
            ...(this.form.getValue('key') && {
              key: {
                $regex: this.form.getValue('key'),
                $options: 'i',
              },
            }),
            ...(this.form.getValue('value') && {
              translations: {
                $elemMatch: {
                  value: {
                    $regex: this.form.getValue('value'),
                  },
                },
              },
            }),
          },
          page: this.pagination.current,
          size: this.pagination.pageSize,
          ...(this.sort &&
            this.sort.field && {
              sort: {
                [this.sort.field]: this.sort.order === 'descend' ? 'desc' : 'asc',
              },
            }),
        },
      })
      .then(() => {
        if (this.fetch.data) {
          this.items = this.fetch.data.docs;
          this.pagination = { ...this.pagination, total: this.fetch.data.total };
        } else {
          this.items = [];
          this.pagination = { ...this.pagination, total: 0 };
        }
      });
  });

  handleSave = action((row, values) => {
    const newTranslations = Object.keys(values)
      .filter((locale) => row.translations.map((translation) => translation.locale).indexOf(locale) < 0)
      .map((locale) => ({ locale: locale, value: values[locale] }));

    this.submit
      .fetch({
        url: `/localization-items/${row.id}`,
        method: 'put',
        body: {
          ...row,
          translations: [
            ...row.translations.map((translation) =>
              values.hasOwnProperty(translation.locale)
                ? {
                    ...translation,
                    locale: translation.locale,
                    value: values[translation.locale],
                  }
                : {
                    ...translation,
                    value: translation.value || '',
                  }
            ),
            ...newTranslations,
          ],
        },
      })
      .then(() => {
        if (this.submit.data) {
          NotificationService.addNotification(`Localization Item updated successfully.`, 'success');
          this.load();
        }
      });
  });

  handleRemove = action((row) => {
    ConfirmationDialogService.show(
      'Delete LocalizationItems',
      `You are about to delete the Item "${row.key}", Do you want to continue ?`,
      () => {
        this.submit
          .fetch({
            url: `/localization-items/${row.id}`,
            method: 'delete',
          })
          .then(() => {
            if (this.submit.data) {
              NotificationService.addNotification(`Localization Item "${row.key}" deleted successfully.`, 'success');
              this.load();
            }
            if (this.submit.error) {
              NotificationService.addNotification(`Error deleting Localization Item.`, 'error');
            }
          });
      }
    );
  });

  handleChange = action((pagination, change, sort, { firstLocaleSelect, secondLocaleSelect }) => {
    this.pagination = (pagination && { ...pagination }) || this.pagination;
    this.sort = (sort && { ...sort }) || this.sort;
    this.form.setValue('firstLocaleSelect', firstLocaleSelect || this.form.getValue('firstLocaleSelect'));
    this.form.setValue('secondLocaleSelect', secondLocaleSelect || this.form.getValue('secondLocaleSelect'));
    (pagination || sort) && this.load();
  });
}

const localizationListService = new LocalizationListService();

export default localizationListService;
